import { icdsAction } from '@/src/api'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Form } from 'grommet'
import { useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useParams, useSearchParams } from 'react-router-dom'
import { FormButton, SelectInputField } from '../../../../components/common'
import RichTextEditor from '../../../../components/common/Editor2'
import { Select } from '../../../../components/common/SearchInput/Select'
import { Modal } from '../../../../components/Modals'
import { relationOptions } from '../../../../constants'
import { useFamily, useIcds } from '../../../../hooks'
import { showToast } from '../../../../utils/toast'
import { familySchema } from '../../../../validation/pDirectory.schema'

type Props = {
  isOpen: boolean
  onClose: () => void
}

const AddFamily = ({ onClose }: Props) => {
  const { patientId, intakeId: id } = useParams<string>()
  const [searchParams] = useSearchParams()
  const intakeId = searchParams.get('intake') || id

  const { createFamily } = useFamily()
  const { AllIcds } = useIcds()

  const [value, setValue] = useState('')
  const module = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: '' }, { align: 'center' }, { align: 'right' }],
      [{ direction: 'rtl' }],
      ['link', 'image'],
      ['clean']
    ]
  }
  const methods = useForm({ resolver: yupResolver(familySchema) })

  const onSubmit = async (data: any) => {
    const payload = {
      data: {
        attributes: {
          ...data
        },
        relationships: intakeId
          ? {
              intake: {
                data: {
                  id: intakeId,
                  type: 'intake'
                }
              },
              patient: {
                data: {
                  id: patientId,
                  type: 'patient'
                }
              }
            }
          : {
              patient: {
                data: {
                  id: patientId,
                  type: 'patient'
                }
              }
            },
        type: 'family_history'
      }
    }
    try {
      await createFamily(payload)
      showToast('Family created successfully')
      onClose()
    } catch (error: any) {
      showToast(error?.response?.data?.errors![0]?.detail || 'an error occurred')
    }
  }

  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} reset={methods.reset} title="Add Family">
        <Form className="flex flex-col md:w-[750px] w-full" onSubmit={methods.handleSubmit(onSubmit)}>
          <Box
            className="!min-h-[auto]"
            flex={false}
            pad={{ horizontal: '32px', vertical: '26px' }}
            style={{
              overflowX: 'hidden',
              overflowY: 'auto'
            }}
            width={'100%'}
          >
            <Box
              style={{
                minHeight: '320px',
                position: 'relative'
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  width: '100%'
                }}
              >
                <Box className="md:w-[750px] w-full pb-4 mx-auto" flex={false} gap={'24px'}>
                  <Box>
                    <SelectInputField
                      label="Relationship"
                      labelKey={'name'}
                      name="relationship"
                      options={relationOptions}
                      valueKey={{ key: 'value', reduce: true }}
                    />
                  </Box>
                  {/* <Box>
                    <FormInputField label="Name" name="full_name" />
                  </Box> */}
                  <Box>
                    <Controller
                      name="medical_conditions"
                      render={({ field: { onChange }, fieldState: { error } }) => (
                        <Select
                          defaultOptions={AllIcds}
                          error={error?.message}
                          getOptionLabel={({ attributes }) => `${attributes.title} - ${attributes.code}`}
                          getOptionValue={({ id }) => id}
                          isAsync
                          isMulti
                          label="Medical Condition"
                          loadOptions={icdsAction}
                          onChange={selectedOptions => {
                            const values = selectedOptions
                              ? selectedOptions?.map((option: any) => option?.attributes?.code)
                              : []
                            onChange(values) // Update the form value
                          }}
                        />
                      )}
                    />
                  </Box>
                  <Box>
                    <RichTextEditor label="Additional Info" modules={module} onChange={setValue} value={value} />
                  </Box>
                </Box>
              </div>
            </Box>
          </Box>
          <Box style={{ background: '#fff', bottom: 0, position: 'sticky', zIndex: 20 }}>
            <FormButton isLoading={methods.formState.isSubmitting} onClose={onClose} submit="save" />
          </Box>
        </Form>
      </Modal>
    </FormProvider>
  )
}

export default AddFamily
